import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { RefreshCw, RotateCcw } from "lucide-react";
import api from "../lib/api";
import { fmtDateTime, relTime } from "../lib/format";
import { useAuth } from "../lib/authStore";
import TopBar from "../components/TopBar";
import StatusPill from "../components/StatusPill";
import EmptyState from "../components/EmptyState";
import StageDots from "../components/StageDots";
import ArtifactView from "../components/ArtifactView";
import ApprovalActions from "../components/ApprovalActions";
import Modal from "../components/Modal";

const LIVE_STATUSES = new Set(["in_progress", "in_review", "draft"]);

export default function MissionDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const isDirector = user?.role === "director";
  const [mission, setMission] = useState(null);
  const [stages, setStages] = useState([]);
  const [artifacts, setArtifacts] = useState([]);
  const [agentRuns, setAgentRuns] = useState([]);
  const [qaRuns, setQaRuns] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [returnModalOpen, setReturnModalOpen] = useState(false);
  const [returnReason, setReturnReason] = useState("");
  const [revisedBrief, setRevisedBrief] = useState("");
  const [returnSubmitting, setReturnSubmitting] = useState(false);
  const [returnError, setReturnError] = useState(null);
  const polling = useRef(false);

  const load = useCallback(async () => {
    try {
      const [{ data: m }, { data: st }, { data: ar }, { data: rn }, { data: qa }, { data: lg }, { data: apr }] =
        await Promise.all([
          api.get(`/missions/${id}`),
          api.get(`/missions/${id}/stages`),
          api.get(`/missions/${id}/artifacts`),
          api.get(`/missions/${id}/agent_runs`),
          api.get(`/missions/${id}/qa_runs`),
          api.get(`/missions/${id}/ledger`),
          api.get(`/approvals?status=all&mission_id=${id}`),
        ]);
      setMission(m);
      setStages(st);
      setArtifacts(ar);
      setAgentRuns(rn);
      setQaRuns(qa);
      setLedger(lg);
      setApprovals(apr);
    } catch (e) {
      if (e?.response?.status === 404) setNotFound(true);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!mission) return;
    const live = LIVE_STATUSES.has(mission.status);
    if (!live || polling.current) return;
    polling.current = true;
    const tick = async () => {
      await load();
      const cur = await api.get(`/missions/${id}`).then((r) => r.data).catch(() => null);
      if (cur && LIVE_STATUSES.has(cur.status)) {
        timer = setTimeout(tick, 3000);
      } else {
        polling.current = false;
      }
    };
    let timer = setTimeout(tick, 3000);
    return () => {
      polling.current = false;
      clearTimeout(timer);
    };
  }, [mission, id, load]);

  if (notFound) {
    return (
      <>
        <TopBar title="Mission" />
        <div className="panel"><EmptyState title="Mission not found." /></div>
      </>
    );
  }
  if (!mission) return <TopBar title="Loading…" />;

  const stagesByKey = {};
  for (const s of stages) stagesByKey[s.key] = s.status;
  const pending = approvals.filter((a) => a.decision === "pending");
  const totalCost = agentRuns.reduce((acc, r) => acc + (r.usd_estimate || 0), 0);
  const totalTokens = agentRuns.reduce((acc, r) => acc + (r.tokens_input || 0) + (r.tokens_output || 0), 0);

  // Latest QA report artifact for the pass/inconclusive/fail summary.
  const latestQaReport = [...artifacts]
    .filter((a) => a.kind === "qa_report")
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
  const qaCounts = { pass: 0, inconclusive: 0, fail: 0 };
  if (latestQaReport) {
    for (const pc of latestQaReport.content?.per_criterion || []) {
      if (pc.status in qaCounts) qaCounts[pc.status] += 1;
    }
  }
  const latestQaRun = qaRuns[0];
  const qaBlocked =
    mission.status === "blocked" &&
    latestQaRun &&
    (latestQaRun.verdict === "fail" || latestQaRun.verdict === "needs_revision");

  const openReturnModal = () => {
    setReturnReason("");
    setRevisedBrief(mission.brief || "");
    setReturnError(null);
    setReturnModalOpen(true);
  };

  const submitReturnToIntake = async () => {
    if (!returnReason.trim()) {
      setReturnError("Reason is required.");
      return;
    }
    setReturnSubmitting(true);
    setReturnError(null);
    try {
      const body = { reason: returnReason.trim() };
      if (revisedBrief.trim() && revisedBrief.trim() !== (mission.brief || "").trim()) {
        body.revised_brief = revisedBrief.trim();
      }
      await api.post(`/missions/${id}/return-to-intake`, body);
      setReturnModalOpen(false);
      // small delay so orchestrator has spawned the new Product run before we reload.
      setTimeout(() => { load(); }, 500);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setReturnError(typeof d === "string" ? d : d?.message || "Failed to return to intake.");
    } finally {
      setReturnSubmitting(false);
    }
  };

  return (
    <>
      <TopBar
        title={mission.title}
        subtitle={`#${mission.id.slice(0, 8)} · created ${relTime(mission.created_at)}`}
        actions={
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <StatusPill status={mission.status} />
            {LIVE_STATUSES.has(mission.status) ? (
              <span className="pill cyan"><span className="loader" style={{ width: 10, height: 10 }} /> live</span>
            ) : null}
            <button className="btn small" onClick={load} data-testid="mission-refresh"><RefreshCw size={12} /> Refresh</button>
          </div>
        }
      />

      <div className="detail-meta">
        <div className="cell"><small>Priority</small><b>{mission.priority.toUpperCase()}</b></div>
        <div className="cell"><small>Current Stage</small><b>{mission.current_stage}</b></div>
        <div className="cell"><small>Total tokens</small><b>{totalTokens}</b></div>
        <div className="cell"><small>Total cost</small><b>${totalCost.toFixed(4)}</b></div>
      </div>

      {pending.length ? (
        <div className="panel" style={{ marginBottom: 16, borderColor: "rgba(139,92,246,0.4)" }}>
          <div className="panel-head">
            <div><h2>Director approval required</h2><p>Mission is paused until you decide.</p></div>
            <span className="pill purple">{pending.length}</span>
          </div>
          <div className="panel-body">
            {pending.map((a) => (
              <div className="wide-row" key={a.id} data-testid={`approval-${a.id}`}>
                <div>
                  <b>{a.kind.replace(/_/g, " ")}</b>
                  <p>{a.reason || "Approval requested"}</p>
                  <small className="hint">stage: {a.stage_key} · requested {relTime(a.requested_at)}</small>
                </div>
                <ApprovalActions approval={a} onDecided={load} />
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <div className="panel" style={{ marginBottom: 16 }}>
        <div className="panel-head">
          <div><h2>Brief</h2><p>Director&apos;s mission statement.</p></div>
        </div>
        <div className="panel-body" data-testid="mission-brief">{mission.brief}</div>
      </div>

      {latestQaReport ? (
        <div className="panel" style={{ marginBottom: 16 }} data-testid="qa-counts-panel">
          <div className="panel-head">
            <div>
              <h2>QA verdict summary</h2>
              <p>Latest independent evaluation · per-criterion breakdown.</p>
            </div>
            <span className={`pill ${latestQaRun?.verdict === "pass" ? "green" : latestQaRun?.verdict === "fail" ? "red" : "amber"}`}>
              {latestQaRun?.verdict || "—"}
            </span>
          </div>
          <div className="panel-body">
            <div className="grid-3">
              <div className="wide-row" data-testid="qa-count-pass">
                <div><b><span className="pill green">Pass</span></b><p className="hint" style={{ fontSize: 20, marginTop: 4 }}>{qaCounts.pass}</p></div>
              </div>
              <div className="wide-row" data-testid="qa-count-inconclusive">
                <div><b><span className="pill amber">Inconclusive</span></b><p className="hint" style={{ fontSize: 20, marginTop: 4 }}>{qaCounts.inconclusive}</p></div>
              </div>
              <div className="wide-row" data-testid="qa-count-fail">
                <div><b><span className="pill red">Fail</span></b><p className="hint" style={{ fontSize: 20, marginTop: 4 }}>{qaCounts.fail}</p></div>
              </div>
            </div>
            {qaBlocked && isDirector ? (
              <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
                <button
                  className="btn small"
                  onClick={async () => {
                    try {
                      await api.post(`/missions/${id}/retry-stage/engineering`);
                      await load();
                    } catch (e) {
                      console.error("retry engineering failed", e);
                    }
                  }}
                  data-testid="retry-engineering-btn"
                >
                  <RefreshCw size={12} /> Retry engineering
                </button>
                <button
                  className="btn small primary"
                  onClick={openReturnModal}
                  data-testid="return-to-intake-btn"
                >
                  <RotateCcw size={12} /> Return to intake…
                </button>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      <div className="panel" style={{ marginBottom: 16 }}>
        <div className="panel-head">
          <div><h2>Stage Lifecycle</h2><p>Delivery gates from intake to production.</p></div>
        </div>
        <div className="panel-body">
          <StageDots stagesByKey={stagesByKey} />
          <div className="grid-3" style={{ marginTop: 14 }}>
            {stages.map((s) => (
              <div className="wide-row" key={s.key} data-testid={`stage-${s.key}`}>
                <div>
                  <b>{s.key}</b>
                  <p><span className={`pill ${s.status === "passed" ? "green" : s.status === "in_progress" ? "blue" : s.status === "failed" ? "red" : s.status === "skipped" ? "purple" : "dim"}`}>{s.status}</span></p>
                  {s.started_at ? <small className="hint">started {relTime(s.started_at)}</small> : null}
                  {s.completed_at ? <small className="hint"> · completed {relTime(s.completed_at)}</small> : null}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {artifacts.length ? (
        <div className="panel" style={{ marginBottom: 16 }}>
          <div className="panel-head">
            <div><h2>Artifacts</h2><p>Evidence produced by each stage. Newest last.</p></div>
            <span className="pill dim">{artifacts.length}</span>
          </div>
          {artifacts.map((a) => (
            <div key={a.id} data-testid={`artifact-${a.kind}`} style={{ borderBottom: "1px solid var(--line)" }}>
              <div className="panel-head" style={{ borderBottom: 0, paddingBottom: 6 }}>
                <div>
                  <h2 style={{ fontSize: 13 }}>{a.title}</h2>
                  <p>{a.kind} · stage {a.stage_key} · <span className="mono">{a.content_hash.slice(0, 14)}…</span> · {relTime(a.created_at)}</p>
                </div>
              </div>
              <div className="panel-body"><ArtifactView artifact={a} /></div>
            </div>
          ))}
        </div>
      ) : null}

      <div className="grid-2" style={{ marginBottom: 16 }}>
        <div className="panel">
          <div className="panel-head">
            <div><h2>Agent Runs</h2><p>Per-agent invocations with tokens/cost.</p></div>
            <span className="pill dim">{agentRuns.length}</span>
          </div>
          <div className="panel-body">
            {agentRuns.length === 0 ? <EmptyState title="No agent runs yet." /> :
              agentRuns.map((r) => (
                <div className="wide-row" key={r.id} data-testid={`run-${r.stage_key}-${r.status}`}>
                  <div>
                    <b>{r.stage_key} · <span className={`pill ${r.status === "succeeded" ? "green" : r.status === "failed" ? "red" : r.status === "running" ? "blue" : "dim"}`}>{r.status}</span></b>
                    <p className="mono" style={{ fontSize: 11 }}>{r.provider}/{r.model} · {r.tokens_input}→{r.tokens_output} tok · ${r.usd_estimate.toFixed(4)} · {r.latency_ms}ms</p>
                    {r.error ? <small className="hint" style={{ color: "#ffb4be" }}>{r.error}</small> : null}
                  </div>
                  <small className="hint">{relTime(r.started_at || r.completed_at)}</small>
                </div>
              ))
            }
          </div>
        </div>

        <div className="panel">
          <div className="panel-head">
            <div><h2>QA Runs</h2><p>Independent verdicts. SoD-enforced.</p></div>
            <span className="pill dim">{qaRuns.length}</span>
          </div>
          <div className="panel-body">
            {qaRuns.length === 0 ? <EmptyState title="No QA runs yet." /> :
              qaRuns.map((q) => (
                <div className="wide-row" key={q.id}>
                  <div>
                    <b><span className={`pill ${q.verdict === "pass" ? "green" : q.verdict === "fail" ? "red" : "amber"}`}>{q.verdict}</span></b>
                    <p className="hint">evaluator: <span className="mono">{q.evaluator_agent_id.slice(0, 8)}</span></p>
                  </div>
                  <small className="hint">{relTime(q.created_at)}</small>
                </div>
              ))
            }
          </div>
        </div>
      </div>

      <div className="panel">
        <div className="panel-head">
          <div><h2>Activity for this Mission</h2><p>Attributable events in order.</p></div>
          <span className="pill dim">{ledger.length}</span>
        </div>
        <div className="panel-body" style={{ maxHeight: 360, overflow: "auto" }}>
          {ledger.length === 0 ? <EmptyState title="No events yet." /> :
            ledger.map((e) => (
              <div className="wide-row" key={e.id}>
                <div>
                  <b className="mono" style={{ fontSize: 12 }}>{e.type}</b>
                  <p className="hint" style={{ fontSize: 11 }}>{e.actor_type}·{e.actor_id.slice(0, 8)}</p>
                </div>
                <small className="hint">{fmtDateTime(e.ts)}</small>
              </div>
            ))
          }
        </div>
      </div>

      {returnModalOpen ? (
        <Modal
          title="Return mission to intake"
          eyebrow="DIRECTOR ACTION"
          onClose={() => setReturnModalOpen(false)}
          testId="return-to-intake-modal"
        >
          <p className="hint" style={{ marginTop: 8 }}>
            The current mission will be reset to the intake stage and Product will re-run.
            Prior artifacts and QA runs stay in the audit trail. Provide a reason so the
            ledger can attribute the decision.
          </p>
          <label style={{ display: "block", marginTop: 12 }}>
            <small className="hint">Reason <span style={{ color: "#ffb4be" }}>*</span></small>
            <textarea
              rows={3}
              value={returnReason}
              onChange={(e) => setReturnReason(e.target.value)}
              placeholder="Why is this mission being sent back?"
              data-testid="return-reason"
              style={{ width: "100%", marginTop: 6 }}
            />
          </label>
          <label style={{ display: "block", marginTop: 12 }}>
            <small className="hint">Revised brief (optional)</small>
            <textarea
              rows={5}
              value={revisedBrief}
              onChange={(e) => setRevisedBrief(e.target.value)}
              placeholder="Leave unchanged to re-run against the original brief."
              data-testid="return-revised-brief"
              style={{ width: "100%", marginTop: 6 }}
            />
          </label>
          {returnError ? <p className="hint" style={{ color: "#ffb4be", marginTop: 10 }}>{returnError}</p> : null}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
            <button className="btn" onClick={() => setReturnModalOpen(false)}>Cancel</button>
            <button
              className="btn primary"
              disabled={returnSubmitting}
              onClick={submitReturnToIntake}
              data-testid="return-submit"
            >
              {returnSubmitting ? "Submitting…" : "Return to intake"}
            </button>
          </div>
        </Modal>
      ) : null}
    </>
  );
}
